import { useState } from 'react'
import { SisetuResult } from 'interface/sisetu'

import { SisetuPreviewBasicPage } from './basicPage'
import { SisetuPreviewDoctorsPage } from './doctorsPage'
import { SisetuPreviewDpcPage } from './dpcPage'
import { SisetuPreviewCancerPage } from './cancerPage'
import { SisetuPreviewTrialPage } from './trialPage'
import { SisetuPreviewJournalPage } from './journalPage'
import { SisetuPreviewResearchPage } from './researchPage'

import { Stack, StackProps, Box, Tabs, Tab, BoxProps, TabProps, useTheme, useMediaQuery } from '@mui/material'
import { SisetuPreviewTab, SisetuPreviewTabType } from '../types'
import { useSisetuPreview } from '../container'

const alriaLabel = 'sisetu-preview'

const SisetuPreviewTabBtn = (props: TabProps) => (
  <Tab
    {...props}
    id={`${alriaLabel}-${props.value}`}
    aria-controls={`${alriaLabel}-tabpanel-${props.value}`}
    disableRipple
    sx={{
      textTransform: 'none'
    }}
  />
)

type SisetuPreviewTabPageProps = {
  tabName: SisetuPreviewTabType
  currentTabName: SisetuPreviewTabType
  children?: React.ReactNode
} & BoxProps
const SisetuPreviewTabPage = ({
  tabName,
  currentTabName,
  children,
  ...other
}: SisetuPreviewTabPageProps) => (
  <Box
    role="tabpanel"
    hidden={currentTabName !== tabName}
    id={`${alriaLabel}-${tabName}`}
    aria-labelledby={`${alriaLabel}-tab-${tabName}`}
    sx={{
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
      overflow: 'auto',
    }}
    {...other}
  >
    {currentTabName === tabName && <>{children}</>}
  </Box>
)

type SisetuPreviewBodyProps = {
  sisetu: SisetuResult
  container: ReturnType<typeof useSisetuPreview>
  defaultPage: SisetuPreviewTabType | null
} & StackProps
export const SisetuPreviewBody = ({
  sisetu,
  container,
  defaultPage,
  ...others
}: SisetuPreviewBodyProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down('sm'))
  const [tabName, setTabName] = useState<SisetuPreviewTabType>(defaultPage ?? SisetuPreviewTab.Basic)
  return (
    <Stack
      direction="column"
      position='relative'
      overflow='hidden'
      boxSizing='border-box'
      {...others}
    >
      <Stack flexShrink={0} direction="row">
        <Tabs
          value={tabName}
          onChange={(ev, value) => setTabName(value as SisetuPreviewTabType)}
          sx={{
            flexGrow: 1,
            borderBottom: 1,
            borderColor: 'divider',
            paddingX: isSmallMedia ? 0 : '1em'
          }}
          //variant="scrollable"
          //scrollButtons="auto"
          //allowScrollButtonsMobile
          aria-label={`${alriaLabel}-tabs`}
        >
          <SisetuPreviewTabBtn label="基本情報" value={SisetuPreviewTab.Basic} />
          <SisetuPreviewTabBtn label="医師" value={SisetuPreviewTab.Doctors} />
          {(sisetu.score?.dpc || tabName === SisetuPreviewTab.Dpc) && <SisetuPreviewTabBtn label="DPC" value={SisetuPreviewTab.Dpc} />}
          {(sisetu.score?.cancer || tabName === SisetuPreviewTab.Cancer) && <SisetuPreviewTabBtn label="がん" value={SisetuPreviewTab.Cancer} />}
          {(sisetu.score?.trial || tabName === SisetuPreviewTab.Trial) && <SisetuPreviewTabBtn label="治験" value={SisetuPreviewTab.Trial} />}
          {(sisetu.score?.research || tabName === SisetuPreviewTab.Research) && <SisetuPreviewTabBtn label="研究" value={SisetuPreviewTab.Research} />}
          {(sisetu.score?.journal || tabName === SisetuPreviewTab.Journal) && <SisetuPreviewTabBtn label="論文" value={SisetuPreviewTab.Journal} />}
        </Tabs>
      </Stack>

      <Box flexGrow={1} overflow='hidden' position='relative'>
        <SisetuPreviewTabPage
          currentTabName={tabName}
          tabName={SisetuPreviewTab.Basic}
        >
          <SisetuPreviewBasicPage sisetu={sisetu} container={container}/>
        </SisetuPreviewTabPage>

        <SisetuPreviewTabPage
          currentTabName={tabName}
          tabName={SisetuPreviewTab.Doctors}
        >
          <SisetuPreviewDoctorsPage sisetu={sisetu} container={container}/>
        </SisetuPreviewTabPage>

        <SisetuPreviewTabPage
          currentTabName={tabName}
          tabName={SisetuPreviewTab.Dpc}
        >
          <SisetuPreviewDpcPage sisetu={sisetu} container={container}/>
        </SisetuPreviewTabPage>

        <SisetuPreviewTabPage
          currentTabName={tabName}
          tabName={SisetuPreviewTab.Cancer}
        >
          <SisetuPreviewCancerPage sisetu={sisetu} container={container}/>
        </SisetuPreviewTabPage>

        <SisetuPreviewTabPage
          currentTabName={tabName}
          tabName={SisetuPreviewTab.Trial}
        >
          <SisetuPreviewTrialPage sisetu={sisetu} container={container}/>
        </SisetuPreviewTabPage>

        <SisetuPreviewTabPage
          currentTabName={tabName}
          tabName={SisetuPreviewTab.Research}
        >
          <SisetuPreviewResearchPage sisetu={sisetu} container={container}/>
        </SisetuPreviewTabPage>

        <SisetuPreviewTabPage
          currentTabName={tabName}
          tabName={SisetuPreviewTab.Journal}
        >
          <SisetuPreviewJournalPage sisetu={sisetu} container={container}/>
        </SisetuPreviewTabPage>
      </Box>
    </Stack>
  )
}
